/**
 * File import utilities for SOP Assessment
 * Handles parsing of JSON and potentially PDF files to extract test values
 */

import type { FormValues } from '@/lib/types';

export interface ImportedData {
  [key: string]: any;
}

/**
 * Parse JSON file and extract SOP test values
 * Expects JSON with keys matching FormValues (e.g., "L_CervicalAxialRotation", "R_ShoulderER", etc.)
 */
export async function parseJsonFile(file: File): Promise<ImportedData> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const parsed = JSON.parse(content);
        resolve(parsed);
      } catch (error) {
        reject(new Error(`Failed to parse JSON file: ${error}`));
      }
    };
    
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    
    reader.readAsText(file);
  });
}

/**
 * Extract relevant SOP test values from imported data
 * Filters to only include keys that exist in FormValues
 */
export function extractSopValues(data: ImportedData, validKeys: string[]): Partial<FormValues> {
  const result: Partial<FormValues> = {};
  
  for (const key of validKeys) {
    if (key in data && data[key] !== null && data[key] !== undefined) {
      result[key as keyof FormValues] = data[key];
    }
  }
  
  return result;
}

/**
 * Validate that imported data contains reasonable test values
 * Basic checks: numbers within expected ranges
 */
export function validateImportedValues(values: Partial<FormValues>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Check for empty data
  if (Object.keys(values).length === 0) {
    errors.push('No valid test values found in imported file');
    return { valid: false, errors };
  }
  
  // Numeric range check for goniometer-style fields; strings (selects, grounding labels) skip this
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === 'number') {
      if (value < -180 || value > 360) {
        errors.push(`${key}: value ${value} is outside expected range (-180–360)`);
      }
    }
  }
  
  return { valid: errors.length === 0, errors };
}
